import { token } from '@atlaskit/tokens';
import { Divider } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';

import { ContentfulInstructor } from 'src/graphql/queries/GetInstructorLedTraining';

interface InstructorIntroProps {
  instructor?: ContentfulInstructor | null;
  isFree?: boolean;
}

export const InstructorIntro = ({ instructor, isFree = false }: InstructorIntroProps) => {
  if (!instructor) {
    return null;
  }

  const imageUrl = instructor.image?.cdnUrl || instructor.image?.image?.url || '';
  const { imageAlt, bio, contactEmail, locationValue } = instructor;

  return (
    <section css={[wrapperStyles, isFree && wrapperStylesFree]}>
      <div css={cardStyles}>
        <div css={[isFree ? imageWrapperStyles : imageContainerStylesPaid]}>
          <img src={imageUrl} alt={imageAlt || ''} css={[isFree ? imageStyles : imageStylesPaid]} />
        </div>

        <div css={contentStyles}>
          <h6 css={headingStyles}>Meet your instructor</h6>
          <div css={bioParagraphStyles}>
            {bio?.map((paragraph: string, index: number) => (
              <p key={`instructor-bio-${index}`} css={bioParagraphStyles}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <div css={dividerStyles}>
          <Divider color={token('color.border')} />
        </div>

        <div css={infoContainerStyles}>
          <div css={infoItemStyles}>
            <p css={infoLabelStyles}>Contact</p>
            <p css={infoValueLinkStyles}>{contactEmail}</p>
          </div>
          <div css={infoItemStyles}>
            <p css={infoLabelStyles}>Location</p>
            <p css={infoValueStyles}>{locationValue}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const dividerStyles = css({
  width: '100%',
});

const wrapperStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '1.5rem',
  maxWidth: '24rem',
  minWidth: '24rem',
  [media.below.sm]: {
    margin: 0,
    maxWidth: '100%',
    minWidth: 'auto',
    width: '100%',
  },
});

const wrapperStylesFree = css({
  paddingTop: '4rem',
});

const cardStyles = css({
  display: 'flex',
  paddingBottom: '1rem',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '1rem',
  borderRadius: '0.5rem',
  background: token('elevation.surface'),
  boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 1px 1px 0 rgba(30, 31, 33, 0.25)',
});

const imageContainerStylesPaid = css({
  display: 'flex',
  width: '100%',
  maxHeight: '6rem',
  padding: '1rem',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const imageStylesPaid = css({
  objectFit: 'contain',
});

const imageWrapperStyles = css({
  display: 'flex',
  height: '3.625rem',
  padding: '0.25rem 0',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center',
  alignSelf: 'stretch',
});

const imageStyles = css({
  width: 'auto',
  maxHeight: '6.875rem',
  flexShrink: 0,
  borderRadius: '12.5rem',
});

const contentStyles = css({
  display: 'flex',
  padding: '0 1rem',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.5rem',
  alignSelf: 'stretch',
});

const headingStyles = css({
  alignSelf: 'stretch',
  color: token('color.text.accent.lime'),
  textAlign: 'center',
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '1rem',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '1.5rem',
});

const bioParagraphStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '0.375rem',
  alignSelf: 'stretch',
  '& p': {
    color: token('color.text'),
    fontFamily: 'Inter, sans-serif',
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '1.5rem',
  },
  '& p:last-child': {
    marginBottom: 0,
  },
});

const infoContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '0.5rem',
});

const infoItemStyles = css({
  display: 'flex',
  width: '100%',
  padding: '0 1rem',
  alignItems: 'center',
  gap: '1rem',
});

const infoLabelStyles = css({
  color: token('color.text'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '1.25rem',
});

const infoValueLinkStyles = css({
  color: token('color.text.brand'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '1.25rem',
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
  margin: 0,
});

const infoValueStyles = css({
  color: token('color.text'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '1.25rem',
  margin: 0,
});
